import {
  Target,
  AlertTriangle,
  FileText,
  Palette,
  Code2,
  Presentation,
  Bot,
  Brain,
  TrendingUp,
  MessageSquare,
  CheckCircle2,
  Megaphone,
  Users,
  ListChecks,
  Trophy,
  Utensils,
  Sparkles,
  Monitor,
  Workflow,
} from 'lucide-react';

// Purpose — the "why"
const OBJECTIVES = [
  'Drive cross-functional collaboration across Product, UX, Engineering, and Operations',
  'Embed AI-first ways of working',
  'Generate real, deployable POCs',
  'Identify ideas and teams for fast-track incubation into the Byte roadmap',
];

const SUCCESS_CRITERIA = [
  { title: 'Demonstrated AI Adoption', desc: 'AI used meaningfully in the workflow' },
  { title: 'Cross-Functional Collaboration', desc: 'Strong teamwork across roles' },
];

// AI-First Mandate — where teams must use AI and where it becomes a bonus
const AI_WORKFLOW = [
  { label: 'PRD Creation', icon: FileText },
  { label: 'UX Design', icon: Palette },
  { label: 'Coding', icon: Code2 },
  { label: 'Presentation', icon: Presentation },
];

const AI_PRODUCT = [
  { label: 'Automation', icon: Bot },
  { label: 'Decision Support', icon: Brain },
  { label: 'Prediction', icon: TrendingUp },
  { label: 'Natural Interfaces', icon: MessageSquare },
];

// Anti-patterns — what judges penalise
const ANTI_PATTERNS = [
  { title: 'Dashboard Thinking', desc: "Displays info but doesn't drive action." },
  { title: 'Chatbot as a Feature', desc: 'AI is cosmetic, not functional.' },
  { title: 'Digitizing the Same Process', desc: 'No step-change improvement.' },
  { title: 'More Screens, More Complexity', desc: 'Increases friction instead of reducing it.' },
  { title: 'AI Without Purpose', desc: 'No measurable impact.' },
  { title: 'Incremental Improvement Disguised as Innovation', desc: 'Does not justify investment or rollout.' },
  { title: 'No Path to Reality', desc: 'Not actionable in real operations.' },
  { title: 'Slides Over Substance', desc: 'Execution is the primary judging signal.' },
];

// Themes — platform-wide strategic focus areas
const THEMES = [
  {
    n: 1,
    title: 'Reinvent Restaurant Operations',
    icon: Utensils,
    tint: 'bg-amber-50 border-amber-200',
  },
  {
    n: 2,
    title: 'Reimagine the Frontline Experience',
    icon: Monitor,
    tint: 'bg-blue-50 border-blue-200',
  },
  {
    n: 3,
    title: 'Reinvent Digital Ordering & Customer Experience',
    icon: Sparkles,
    tint: 'bg-violet-50 border-violet-200',
  },
  {
    n: 4,
    title: 'Reinvent How We Build & Run Products',
    icon: Workflow,
    tint: 'bg-emerald-50 border-emerald-200',
  },
];

const KEY_RULES = [
  'AI in workflow is mandatory',
  'Demo required',
  'One submission per team',
];

const TEAM_STRUCTURE = [
  { label: 'Team size', value: '6 members max' },
  {
    label: 'Team composition',
    value: 'A balanced mix of Product, UX, Engineering, and QC roles',
  },
  { label: 'Demo requirement', value: 'Each team must designate 2 presenters' },
];

export function MandatePage() {
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <header className="mb-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-2.5 py-1 text-2xs font-semibold uppercase tracking-wider text-rose-700">
          <Megaphone className="h-3 w-3" aria-hidden="true" />
          Platform-wide · every Byte hackathon event
        </span>
        <h1 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">The Mandate</h1>
        <p className="mt-3 max-w-3xl text-base text-ink-secondary">
          Byte hackathons are behavioural and product transformation levers — not just innovation
          theatre. The brief is the same for every event: build with AI, ship something real, win by
          execution.
        </p>
      </header>

      {/* 1. Purpose & Strategic Intent */}
      <section className="mb-10" aria-labelledby="purpose-heading">
        <div className="mb-4 max-w-3xl">
          <span className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
            The why
          </span>
          <h2
            id="purpose-heading"
            className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl"
          >
            Purpose &amp; strategic intent
          </h2>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-ring bg-surface-raised p-5 shadow-card">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-ink" aria-hidden="true" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
                Objectives
              </h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-ink-secondary">
              {OBJECTIVES.map((o, i) => (
                <li key={i} className="flex gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ink"
                  />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-ring bg-surface-raised p-5 shadow-card">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-ink" aria-hidden="true" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
                Success criteria
              </h3>
            </div>
            <ul className="mt-3 space-y-3">
              {SUCCESS_CRITERIA.map((s, i) => (
                <li key={i} className="rounded-xl bg-accent-subtle p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                    <h4 className="font-display text-sm font-bold text-ink">{s.title}</h4>
                  </div>
                  <p className="mt-1 text-xs text-ink-secondary">{s.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 2. AI-First Mandate */}
      <section className="mb-10" aria-labelledby="ai-mandate-heading">
        <div className="mb-4 max-w-3xl">
          <span className="text-2xs font-semibold uppercase tracking-wider text-rose-600">
            Non-negotiable
          </span>
          <h2
            id="ai-mandate-heading"
            className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl"
          >
            AI-first mandate
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            AI is not optional — it is the foundation of how teams build and what they build.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Workflow — Mandatory */}
          <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/60 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-ink">AI in Workflow</h3>
              <span className="rounded-full bg-rose-600 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-white">
                Mandatory
              </span>
            </div>
            <p className="mt-1 text-sm text-ink-secondary">
              Teams must use AI to accelerate at least:
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {AI_WORKFLOW.map((w) => (
                <div
                  key={w.label}
                  className="flex items-center gap-2 rounded-xl border border-rose-200 bg-white p-3"
                >
                  <w.icon className="h-4 w-4 text-rose-600" aria-hidden="true" />
                  <span className="text-sm font-medium text-ink">{w.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Product — Bonus */}
          <div className="rounded-2xl border border-ring bg-surface-raised p-5 shadow-card">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-ink">AI in Product</h3>
              <span className="rounded-full border border-ring bg-surface-sunken px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-ink-secondary">
                Bonus
              </span>
            </div>
            <p className="mt-1 text-sm text-ink-secondary">
              Every solution should include meaningful AI usage:
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {AI_PRODUCT.map((p) => (
                <div
                  key={p.label}
                  className="flex items-center gap-2 rounded-xl border border-ring bg-surface p-3"
                >
                  <p.icon className="h-4 w-4 text-ink" aria-hidden="true" />
                  <span className="text-sm font-medium text-ink">{p.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Declaration banner */}
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-600 p-4 text-white">
          <Megaphone className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p className="text-sm">
            <span className="font-semibold uppercase tracking-wider">Mandatory:</span>{' '}
            Each team must clearly declare how AI was used during development.
          </p>
        </div>
      </section>

      {/* 3. What Will Not Win — Anti-patterns */}
      <section className="mb-10" aria-labelledby="anti-patterns-heading">
        <div className="mb-4 max-w-3xl">
          <span className="text-2xs font-semibold uppercase tracking-wider text-rose-600">
            Traps to avoid
          </span>
          <h2
            id="anti-patterns-heading"
            className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl"
          >
            What will not win
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            Judges will actively penalise these common patterns that{' '}
            <strong className="text-ink">look good but deliver low value.</strong>
          </p>
        </div>
        <ul className="grid gap-2 sm:grid-cols-2" role="list">
          {ANTI_PATTERNS.map((a) => (
            <li
              key={a.title}
              className="flex gap-3 rounded-xl border border-ring bg-surface-raised p-3.5 shadow-card"
            >
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-rose-500"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-ink">{a.title}</h3>
                <p className="mt-0.5 text-xs text-ink-secondary">{a.desc}</p>
              </div>
            </li>
          ))}
        </ul>
        {/* Closing quote — generalised from the Vietnam 2026 edition */}
        <blockquote className="mt-6 rounded-2xl border-l-4 border-rose-500 bg-ink p-5 text-ink-inverse">
          <p className="text-sm leading-relaxed">
            &ldquo;This is not just about building AI products. This is about becoming AI-native
            builders. The best teams will show not just what they built, but how AI helped them
            build <em>faster, smarter, and better</em>.{' '}
            <strong>If it can&apos;t work in the real world, it won&apos;t win.</strong>&rdquo;
          </p>
        </blockquote>
      </section>

      {/* 4. Themes */}
      <section className="mb-10" aria-labelledby="themes-heading">
        <div className="mb-4 max-w-3xl">
          <span className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
            Where to point your team
          </span>
          <h2
            id="themes-heading"
            className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl"
          >
            The four hackathon themes
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">
            Teams choose <strong className="text-ink">one theme only.</strong> Themes are
            intentionally high-level — they drive broad innovation while staying outcome-focused.
          </p>
        </div>
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" role="list">
          {THEMES.map((t) => (
            <li
              key={t.n}
              className={`rounded-2xl border p-4 ${t.tint}`}
            >
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/80 text-xs font-bold text-ink">
                  {t.n}
                </span>
                <t.icon className="h-4 w-4 text-ink" aria-hidden="true" />
              </div>
              <p className="mt-3 text-sm font-semibold text-ink">{t.title}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* 5. Rules & Team Structure */}
      <section className="mb-2 grid gap-4 lg:grid-cols-2" aria-labelledby="rules-heading">
        <div className="rounded-2xl border border-ring bg-surface-raised p-5 shadow-card">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-ink" aria-hidden="true" />
            <h2
              id="rules-heading"
              className="text-sm font-semibold uppercase tracking-wider text-ink-secondary"
            >
              Key rules
            </h2>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-ink">
            {KEY_RULES.map((r) => (
              <li key={r} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-ring bg-surface-raised p-5 shadow-card">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-ink" aria-hidden="true" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
              Team structure
            </h2>
          </div>
          <dl className="mt-3 space-y-3 text-sm">
            {TEAM_STRUCTURE.map((t) => (
              <div key={t.label}>
                <dt className="text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
                  {t.label}
                </dt>
                <dd className="mt-0.5 text-ink">{t.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Footer pointer */}
      <p className="mt-8 text-center text-xs text-ink-tertiary">
        This mandate applies to every event on the platform. For the day-to-day workflow, see{' '}
        <a href="/blueprint" className="font-medium text-ink hover:underline">
          Blueprint
        </a>{' '}
        · for mentor and judge guidance, see{' '}
        <a href="/guide" className="font-medium text-ink hover:underline">
          Guide
        </a>
        .
      </p>

    </div>
  );
}
